import { getPluginConfig } from './config';
import {
  ADDRESS_TYPE,
  ERROR_CODES,
  FIND_LIMIT,
  FIND_PATH,
  LOQATE_BASE_URL,
  REQUEST_TIMEOUT_MS,
  RETRIEVE_PATH
} from './constants';

// Every failure, whether Loqate's own error (8 daily limit, 10 surge protector,
// 14 free service limit, 17 per-IP limit, 3 and 24 out of credit, and so on),
// an http error or a timeout, has the same shape: an Error with a string `code`.
export const createLoqateError = (code, message) => {
  const error = new Error(message || `Loqate error ${code}`);
  error.name = 'LoqateError';
  error.code = String(code);
  return error;
};

const buildQuery = params =>
  new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ).toString();

// Aborts on the caller's signal or after REQUEST_TIMEOUT_MS, whichever comes first.
// A caller abort rejects with the fetch AbortError; a timeout with a LoqateError.
const request = async (path, params, { signal } = {}) => {
  const { apiKey } = getPluginConfig();
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort();

  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort);
  }
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(
      `${LOQATE_BASE_URL}${path}?${buildQuery({ Key: apiKey, ...params })}`,
      { signal: controller.signal }
    );
    if (!response.ok) {
      throw createLoqateError(`${ERROR_CODES.HTTP}_${response.status}`);
    }

    const { Items: items = [] } = await response.json();
    const [first] = items;
    if (first && first.Error !== undefined) {
      throw createLoqateError(first.Error, first.Description);
    }
    return items;
  } catch (error) {
    if (timedOut) throw createLoqateError(ERROR_CODES.TIMEOUT, 'Loqate request timed out');
    throw error;
  } finally {
    clearTimeout(timeout);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
};

/**
 * Searches for addresses matching `text`, limited to `countries` (ISO2, comma separated).
 * Passing a `container` id lists every address inside that group instead.
 * Results are { id, type, text, description, isContainer }.
 */
export const find = async ({ text, container, countries, signal } = {}) => {
  // No limit inside a group: a block of flats can hold far more than the first page of
  // results, and every one of them has to be pickable
  const limit = container ? undefined : FIND_LIMIT;
  const items = await request(
    FIND_PATH,
    { Text: text, Container: container, Countries: countries, Limit: limit },
    { signal }
  );

  return items.map(({ Id, Type, Text, Description }) => ({
    id: Id,
    type: Type,
    text: Text,
    description: Description,
    isContainer: Type !== ADDRESS_TYPE
  }));
};

/**
 * Returns Loqate's full address for a Find result id. Retrieve can return one item per
 * language; the first is the address in its own language.
 */
export const retrieve = async (id, { signal } = {}) => {
  const [address] = await request(RETRIEVE_PATH, { Id: id }, { signal });
  if (!address) throw createLoqateError(ERROR_CODES.NO_RESULT, 'Loqate returned no address');
  return address;
};
